import React, { useState, useRef, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, Box, Button } from "@mui/material";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

function ImageCropperModal({ open, onClose, imageSrc, onCropDone }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [userInteracted, setUserInteracted] = useState(false);
  const [fileName, setFileName] = useState("original_logo");

  useEffect(() => {
    if (imageSrc) {
      setCrop(null);
      setUserInteracted(false);
      try {
        const url = new URL(imageSrc);
        const parts = url.pathname.split("/");
        const last = parts[parts.length - 1];
        const nameMatch = last.match(/([^/]+)(\.[a-zA-Z0-9]+)?$/);
        if (nameMatch) setFileName(nameMatch[1]);
      } catch {
        setFileName("original_logo");
      }
    }
  }, [imageSrc]);

  const onImageLoad = useCallback((e) => {
    const { width, height } = e.currentTarget;
    const newCrop = makeAspectCrop(
      centerCrop(
        {
          unit: "%",
          width: 90,
        },
        1 // Aspect ratio: 1:1 square
      ),
      width,
      height
    );
    setCrop(newCrop);
  }, []);

  const handleCropDone = useCallback(async () => {
    if (!userInteracted) {
      const response = await fetch(imageSrc);
      const blob = await response.blob();
      const ext = blob.type.split("/")[1] || "jpeg";
      const safeName = fileName.slice(0, 15);
      const originalFile = new File([blob], `${safeName}.${ext}`, {
        type: blob.type,
      });
      onCropDone({ blob: originalFile, fileUrl: imageSrc });
      onClose();
      return;
    }

    if (!imgRef.current || !crop?.width || !crop?.height) return;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 1);
    });

    const safeName = fileName.slice(0, 15);
    const croppedFile = new File([blob], `${safeName}.jpeg`, {
      type: "image/jpeg",
    });
    const fileUrl = URL.createObjectURL(croppedFile);

    onCropDone({ blob: croppedFile, fileUrl });
    onClose();
  }, [crop, onClose, onCropDone, userInteracted, imageSrc, fileName]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 500,
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 2,
          boxShadow: 24,
        }}
      >
        <div style={{ maxHeight: 400, overflow: "auto" }}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(percentCrop) => {
                setCrop(percentCrop);
                setUserInteracted(true);
              }}
              // aspect={1}
            >
              <img
                ref={imgRef}
                alt="Crop target"
                src={imageSrc}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCropDone}>
            {userInteracted ? "Crop & Save" : "Use Original"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

ImageCropperModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageSrc: PropTypes.string,
  onCropDone: PropTypes.func.isRequired,
};

ImageCropperModal.defaultProps = {
  imageSrc: null,
};

export default ImageCropperModal;
